import { fetchWithCSRF } from "@/helpers/common/csrf";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FiCopy } from "react-icons/fi";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

interface ScopeItem {
    scope: string;
    description: string;
}

interface EndpointDoc {
    method: string;
    path: string;
    scope: string;
    description: string;
}

interface CreatedBy {
    id: string;
    twitch_username: string | null;
    email: string | null;
}

interface ApiKeyItem {
    id: string;
    name: string;
    description: string;
    scopes: string[];
    created_by: CreatedBy | null;
    created_at: string;
    expires_at: string | null;
    is_active: boolean;
    is_expired: boolean;
    last_used_at: string | null;
}

interface CreateApiKeyResponse {
    key: string;
    key_data: ApiKeyItem;
}

const TABLE_PAGE_SIZE = 15;

function formatDateTime(value: string | null): string {
    if (!value) return "-";
    return new Date(value).toLocaleString();
}

function formatCreator(createdBy: CreatedBy | null): string {
    if (!createdBy) return "Unknown";
    return createdBy.twitch_username || createdBy.email || "Unknown";
}

export default function AdminUserApiPage() {
    const [keys, setKeys] = useState<ApiKeyItem[]>([]);
    const [scopes, setScopes] = useState<ScopeItem[]>([]);
    const [endpointDocs, setEndpointDocs] = useState<EndpointDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [newKeyValue, setNewKeyValue] = useState<string | null>(null);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
    const [keysPage, setKeysPage] = useState(1);
    const [docsPage, setDocsPage] = useState(1);

    const docsUrl = `${API_URL}/admin-user-api/docs`;
    const openApiUrl = `${API_URL}/admin-user-api/openapi.json`;

    const sortedKeys = useMemo(
        () =>
            [...keys].sort(
                (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
            ),
        [keys],
    );

    const totalKeysPages = Math.max(
        1,
        Math.ceil(sortedKeys.length / TABLE_PAGE_SIZE),
    );
    const totalDocsPages = Math.max(
        1,
        Math.ceil(endpointDocs.length / TABLE_PAGE_SIZE),
    );

    useEffect(() => {
        setKeysPage((current) => Math.min(current, totalKeysPages));
    }, [totalKeysPages]);

    useEffect(() => {
        setDocsPage((current) => Math.min(current, totalDocsPages));
    }, [totalDocsPages]);

    const paginatedKeys = useMemo(() => {
        const start = (keysPage - 1) * TABLE_PAGE_SIZE;
        const end = start + TABLE_PAGE_SIZE;
        return sortedKeys.slice(start, end);
    }, [sortedKeys, keysPage]);

    const paginatedEndpointDocs = useMemo(() => {
        const start = (docsPage - 1) * TABLE_PAGE_SIZE;
        const end = start + TABLE_PAGE_SIZE;
        return endpointDocs.slice(start, end);
    }, [endpointDocs, docsPage]);

    const sessionKillDocs = useMemo(
        () =>
            endpointDocs.filter(
                (endpoint) => endpoint.scope === "session_kills.write",
            ),
        [endpointDocs],
    );

    const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [scopesRes, keysRes, endpointsRes] = await Promise.all([
                fetchWithCSRF(`${API_URL}/admin-user-api/management/scopes`),
                fetchWithCSRF(`${API_URL}/admin-user-api/management/keys`),
                fetchWithCSRF(`${API_URL}/admin-user-api/management/endpoints`),
            ]);

            if (!scopesRes.ok || !keysRes.ok || !endpointsRes.ok) {
                throw new Error("Failed to load admin user API data.");
            }

            setScopes(await scopesRes.json());
            setKeys(await keysRes.json());
            setEndpointDocs(await endpointsRes.json());
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to load data.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchAll();
    }, []);

    const toggleScope = (scope: string) => {
        setSelectedScopes((current) =>
            current.includes(scope)
                ? current.filter((item) => item !== scope)
                : [...current, scope],
        );
    };

    const handleCreateKey = async () => {
        if (!name.trim()) {
            setError("Please provide a key name.");
            return;
        }

        if (selectedScopes.length === 0) {
            setError("Please select at least one scope.");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const response = await fetchWithCSRF(
                `${API_URL}/admin-user-api/management/keys`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name,
                        description,
                        scopes: selectedScopes,
                        expires_at: expiresAt
                            ? new Date(expiresAt).toISOString()
                            : null,
                    }),
                },
            );

            if (!response.ok) {
                const failure = await response
                    .json()
                    .catch(() => ({ detail: "Failed to create key." }));
                throw new Error(failure.detail || "Failed to create key.");
            }

            const result: CreateApiKeyResponse = await response.json();
            setNewKeyValue(result.key);
            setName("");
            setDescription("");
            setExpiresAt("");
            setSelectedScopes([]);
            await fetchAll();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to create key.",
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (keyId: string) => {
        const confirmed = window.confirm("Deactivate this API key?");
        if (!confirmed) {
            return;
        }

        setError(null);
        try {
            const response = await fetchWithCSRF(
                `${API_URL}/admin-user-api/management/keys/${keyId}/deactivate`,
                {
                    method: "POST",
                },
            );

            if (!response.ok) {
                throw new Error("Failed to deactivate key.");
            }

            await fetchAll();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to deactivate key.",
            );
        }
    };

    const copyNewKey = async () => {
        if (!newKeyValue) {
            return;
        }

        await navigator.clipboard.writeText(newKeyValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    if (loading) {
        return (
            <div className="p-4 text-slate-600 dark:text-slate-300">
                Loading Admin User API…
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    Admin User API
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                    Create scoped API keys for admin-only external integrations
                    and view endpoint documentation.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                    <a
                        href={docsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg bg-blue-600 px-3 py-2 font-medium text-white hover:bg-blue-700"
                    >
                        Open Swagger Docs
                    </a>
                    <a
                        href={openApiUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg border border-slate-300 px-3 py-2 font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                        Open OpenAPI JSON
                    </a>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    How To Use This API
                </h2>
                <div className="mt-4 space-y-4 text-sm text-slate-700 dark:text-slate-300">
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            1. Create a key
                        </h3>
                        <p className="mt-1">
                            Give it a clear name, short description, and only
                            the scopes required for your integration.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            2. Store it securely
                        </h3>
                        <p className="mt-1">
                            The full key is shown once after creation. Save it
                            in your secret manager.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            3. Authenticate requests
                        </h3>
                        <p className="mt-1">
                            Send the key as a Bearer token in the Authorization
                            header.
                        </p>
                        <div className="mt-2 rounded-lg bg-slate-100 p-3 font-mono text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                            Authorization: Bearer
                            vuh_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                        </div>
                    </div>

                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            4. Example request
                        </h3>
                        <div className="mt-2 rounded-lg bg-slate-100 p-3 font-mono text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                            curl -X GET
                            "$BASE_URL/admin-user-api/players/by-twitch-id/123456"
                            <br />
                            -H "Authorization: Bearer
                            vuh_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        </div>
                    </div>

                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            Swagger lock icon
                        </h3>
                        <p className="mt-1">
                            In Swagger UI, an unlocked icon usually means no
                            credentials are currently entered in the UI. It does
                            not mean the endpoint is public.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            Common responses
                        </h3>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li>
                                401: Missing, invalid, expired, or deactivated
                                API key.
                            </li>
                            <li>
                                403: Valid key, but missing the required scope
                                for that endpoint.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Session Kill Counters
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    These endpoints update the active stream session in the
                    same request. If there is no active session, the API
                    returns an error.
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                    {sessionKillDocs.map((endpoint) => (
                        <div
                            key={`${endpoint.method}-${endpoint.path}`}
                            className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-800/60"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <span className="font-semibold text-slate-900 dark:text-slate-100">
                                    {endpoint.path}
                                </span>
                                <span className="rounded-full bg-slate-200 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                    {endpoint.method}
                                </span>
                            </div>
                            <p className="mt-2 text-slate-600 dark:text-slate-300">
                                {endpoint.description}
                            </p>
                            <p className="mt-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                                Scope: {endpoint.scope}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
                    {error}
                </div>
            )}

            {newKeyValue && (
                <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 dark:border-emerald-700 dark:bg-emerald-900/20">
                    <h2 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">
                        New API Key Created
                    </h2>
                    <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                        This key is only shown once. Copy and store it securely.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                        <code className="max-w-full overflow-auto rounded-md bg-emerald-100 px-3 py-2 text-xs text-emerald-900 dark:bg-emerald-800/30 dark:text-emerald-200">
                            {newKeyValue}
                        </code>
                        <button
                            onClick={() => void copyNewKey()}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800"
                        >
                            <FiCopy /> {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Create API Key
                </h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="space-y-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Name
                        </span>
                        <input
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Moderation Bot"
                        />
                    </label>
                    <label className="space-y-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Expires At (optional)
                        </span>
                        <input
                            type="datetime-local"
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                        />
                    </label>
                </div>
                <label className="mt-4 block space-y-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Description
                    </span>
                    <textarea
                        className="min-h-20 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Purpose, owner, and integration details"
                    />
                </label>

                <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Scopes
                    </p>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                        {scopes.map((scope) => (
                            <label
                                key={scope.scope}
                                className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedScopes.includes(
                                        scope.scope,
                                    )}
                                    onChange={() => toggleScope(scope.scope)}
                                    className="mt-0.5"
                                />
                                <span>
                                    <span className="block font-mono text-xs text-slate-900 dark:text-slate-100">
                                        {scope.scope}
                                    </span>
                                    <span className="text-slate-600 dark:text-slate-400">
                                        {scope.description}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <button
                    onClick={() => void handleCreateKey()}
                    disabled={saving}
                    className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                    {saving ? "Creating..." : "Create API Key"}
                </button>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    API Keys
                </h2>
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                        <thead>
                            <tr className="text-left text-slate-600 dark:text-slate-300">
                                <th className="px-3 py-2">Name</th>
                                <th className="px-3 py-2">Description</th>
                                <th className="px-3 py-2">Scopes</th>
                                <th className="px-3 py-2">Created By</th>
                                <th className="px-3 py-2">Expires</th>
                                <th className="px-3 py-2">Last Used</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {paginatedKeys.map((key) => (
                                <tr key={key.id}>
                                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
                                        {key.name}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                        {key.description || "-"}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex max-w-md flex-wrap gap-1">
                                            {key.scopes.map((scope) => (
                                                <span
                                                    key={`${key.id}-${scope}`}
                                                    className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                                >
                                                    {scope}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                        {formatCreator(key.created_by)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                        {formatDateTime(key.expires_at)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                        {formatDateTime(key.last_used_at)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={`rounded-md px-2 py-1 text-xs font-semibold ${
                                                key.is_active && !key.is_expired
                                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                    : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                            }`}
                                        >
                                            {key.is_active
                                                ? key.is_expired
                                                    ? "Expired"
                                                    : "Active"
                                                : "Deactivated"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {key.is_active ? (
                                            <button
                                                onClick={() =>
                                                    void handleDeactivate(
                                                        key.id,
                                                    )
                                                }
                                                className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                                            >
                                                Deactivate
                                            </button>
                                        ) : (
                                            <span className="text-xs text-slate-400">
                                                -
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    <span>
                        Page {keysPage} of {totalKeysPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                setKeysPage((current) =>
                                    Math.max(1, current - 1),
                                )
                            }
                            disabled={keysPage <= 1}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setKeysPage((current) =>
                                    Math.min(totalKeysPages, current + 1),
                                )
                            }
                            disabled={keysPage >= totalKeysPages}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Endpoint Documentation
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Scope requirements for all Admin User API endpoints.
                </p>
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                        <thead>
                            <tr className="text-left text-slate-600 dark:text-slate-300">
                                <th className="px-3 py-2">Method</th>
                                <th className="px-3 py-2">Path</th>
                                <th className="px-3 py-2">Scope</th>
                                <th className="px-3 py-2">Description</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {paginatedEndpointDocs.map((endpoint) => (
                                <tr key={`${endpoint.method}-${endpoint.path}`}>
                                    <td className="px-3 py-2 font-semibold text-slate-900 dark:text-slate-100">
                                        {endpoint.method}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                                        {endpoint.path}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                                        {endpoint.scope}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                        {endpoint.description}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    <span>
                        Page {docsPage} of {totalDocsPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                setDocsPage((current) =>
                                    Math.max(1, current - 1),
                                )
                            }
                            disabled={docsPage <= 1}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setDocsPage((current) =>
                                    Math.min(totalDocsPages, current + 1),
                                )
                            }
                            disabled={docsPage >= totalDocsPages}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
